import React, { useState, useEffect } from 'react';
import { X, Save, Type, Palette, Upload, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { useEditor } from '../../contexts/EditorContext';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const EditorPanel = () => {
  const { selectedElement, setSelectedElement, updateContent } = useEditor();
  const [editedContent, setEditedContent] = useState('');
  const [fontFamily, setFontFamily] = useState('inherit');
  const [fontSize, setFontSize] = useState('inherit');
  const [color, setColor] = useState('#000000');
  const [backgroundColor, setBackgroundColor] = useState('transparent');
  const [uploading, setUploading] = useState(false);

  const fonts = [
    { name: 'Standard', value: 'inherit' },
    { name: 'Arial', value: 'Arial, sans-serif' },
    { name: 'Helvetica', value: 'Helvetica, sans-serif' },
    { name: 'Times New Roman', value: '"Times New Roman", serif' },
    { name: 'Georgia', value: 'Georgia, serif' },
    { name: 'Courier', value: '"Courier New", monospace' },
    { name: 'Verdana', value: 'Verdana, sans-serif' },
    { name: 'Trebuchet', value: '"Trebuchet MS", sans-serif' },
    { name: 'Comic Sans', value: '"Comic Sans MS", cursive' },
    { name: 'Impact', value: 'Impact, sans-serif' }
  ];

  useEffect(() => {
    if (selectedElement) {
      if (selectedElement.type === 'text') {
        setEditedContent(selectedElement.content.text || '');
        setFontFamily(selectedElement.content.fontFamily || 'inherit');
        setFontSize(selectedElement.content.fontSize || 'inherit');
        setColor(selectedElement.content.color || '#000000');
        setBackgroundColor(selectedElement.content.backgroundColor || 'transparent');
      } else if (selectedElement.type === 'image') {
        setEditedContent(selectedElement.content.src || '');
      }
    }
  }, [selectedElement]);

  const handleSave = async () => {
    if (!selectedElement) return;

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Sie müssen angemeldet sein');
      return;
    }

    try {
      let content = {};
      
      if (selectedElement.type === 'text') {
        content = {
          text: editedContent,
          fontFamily,
          fontSize,
          color,
          backgroundColor
        };
      } else if (selectedElement.type === 'image') {
        content = {
          src: editedContent,
          alt: selectedElement.content.alt || ''
        };
      }

      await axios.post(
        `${API_URL}/api/page-content/update`,
        {
          page_id: selectedElement.content.pageId || 'home',
          element_id: selectedElement.id,
          content_type: selectedElement.type,
          content
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Update local state
      updateContent(
        selectedElement.content.pageId || 'home',
        selectedElement.id,
        selectedElement.type,
        content
      );

      toast.success('Änderungen gespeichert!');
      setSelectedElement(null);
      
      // Reload page to show changes
      setTimeout(() => window.location.reload(), 500);
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/products/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      setEditedContent(response.data.url);
      toast.success('Bild hochgeladen!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Fehler beim Hochladen');
    } finally {
      setUploading(false);
    }
  };

  if (!selectedElement) return null;

  return (
    <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-2xl z-50 overflow-y-auto border-l-2 border-blue-600">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">Element bearbeiten</h2>
          <button
            onClick={() => setSelectedElement(null)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Text Editor */}
        {selectedElement.type === 'text' && (
          <div className="space-y-4">
            <div>
              <Label>Text</Label>
              <Textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                rows={6}
                className="w-full"
              />
            </div>

            <div>
              <Label>Schriftart</Label>
              <select
                value={fontFamily}
                onChange={(e) => setFontFamily(e.target.value)}
                className="w-full p-2 border rounded"
              >
                {fonts.map(font => (
                  <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                    {font.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label>Schriftgröße</Label>
              <select
                value={fontSize}
                onChange={(e) => setFontSize(e.target.value)}
                className="w-full p-2 border rounded"
              >
                <option value="inherit">Standard</option>
                <option value="12px">Klein (12px)</option>
                <option value="14px">Normal (14px)</option>
                <option value="16px">Mittel (16px)</option>
                <option value="18px">Groß (18px)</option>
                <option value="24px">Sehr groß (24px)</option>
                <option value="32px">Überschrift (32px)</option>
                <option value="48px">Titel (48px)</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2">
                  <Palette className="h-4 w-4" />
                  Textfarbe
                </Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="w-12 h-10 border rounded cursor-pointer"
                  />
                  <div className="flex-1 grid grid-cols-3 gap-1">
                    <button
                      onClick={() => setColor('#000000')}
                      className="h-10 bg-black rounded border"
                      title="Schwarz"
                    />
                    <button
                      onClick={() => setColor('#FFFFFF')}
                      className="h-10 bg-white rounded border-2 border-gray-300"
                      title="Weiß"
                    />
                    <button
                      onClick={() => setColor('#C4A084')}
                      className="h-10 rounded border"
                      style={{ backgroundColor: '#C4A084' }}
                      title="Gold"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <Palette className="h-4 w-4" />
                  Hintergrund
                </Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={backgroundColor === 'transparent' ? '#FFFFFF' : backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="w-12 h-10 border rounded cursor-pointer"
                  />
                  <button
                    onClick={() => setBackgroundColor('transparent')}
                    className="flex-1 h-10 border rounded text-xs"
                  >
                    Transparent
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Image Editor */}
        {selectedElement.type === 'image' && (
          <div className="space-y-4">
            <div>
              <Label>Bild-URL</Label>
              <Input
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                placeholder="https://..."
              />
            </div>

            <div>
              <Label>Oder Bild hochladen</Label>
              <div className="mt-2">
                <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded cursor-pointer hover:bg-gray-50">
                  <Upload className="h-5 w-5" />
                  <span>{uploading ? 'Lädt hoch...' : 'Datei auswählen'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              </div>
            </div>

            {editedContent && (
              <div>
                <Label>Vorschau</Label>
                <img src={editedContent} alt="Vorschau" className="w-full rounded border mt-2" />
              </div>
            )}
          </div>
        )}

        {/* Save Button */}
        <div className="mt-6 flex gap-2">
          <Button onClick={handleSave} className="flex-1">
            <Save className="h-4 w-4 mr-2" />
            Speichern
          </Button>
          <Button variant="outline" onClick={() => setSelectedElement(null)}>
            Abbrechen
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditorPanel;
